import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetColorIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as DownloadIcon } from '@elementary/common-ui/assets/download.svg';
import { Button, Flex, FlexColumn } from '@elementary/common-ui/components';
import Table, {
  QuickFilter,
  TableFilters,
  TableEmptyState,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import { FilterOption } from '@elementary/common-ui/components/FilterSelector';
import Humanize from 'humanize-plus';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  DAYS_BACK,
  COVERAGE_TABLE_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import AssetsTableActionMenu from 'shared/components/AssetsTableActionMenu';
import { EDITABLE_ASSET_TYPES } from 'shared/constants';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import BulkEditWizard from '../BulkEditWizard';
import { BulkEditProperty } from '../BulkEditWizard/types';
import { CatalogFilterOptions } from '../types';
import useCatalogSidePanel from '../../hooks/useCatalogSidePanel';
import {
  useCoverageFilterOptions,
  useCoverageTableData,
  useCoverageTableColumns,
} from './hooks';
import { COVERAGE_TABLE_STORAGE_KEY } from './constants';
import { CoverageTableRow } from './types';
import CoverageSummary from './CoverageSummary';

const TableWrapper = styled(FlexColumn)`
  position: relative;
  background-color: ${({ theme }) => theme.palette.common.white};
  overflow: hidden;
  width: 100%;
  height: 100%;
  gap: 24px;
`;

const TableHeader = styled(Flex)`
  gap: 24px;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

const CoverageTable = () => {
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [bulkEditWizardEditProperty, setBulkEditWizardEditProperty] = useState<
    BulkEditProperty | undefined
  >();
  const [filteredTableData, setFilteredTableData] = useState<
    CoverageTableRow[]
  >([]);
  const { urlFilters } = useUrlFilters({ urlKey: COVERAGE_TABLE_FILTERS });

  const [searchParams] = useSearchParams();
  const daysBack = parseInt(searchParams.get(DAYS_BACK) || '7', 10);

  const { openSidePanel, closeSidePanel, selectedId } = useSidePanelParams();
  useCatalogSidePanel();
  const hasPermission = useEnvPermission();
  const canEdit = hasPermission('EDIT_ASSETS');
  const tableColumns = useCoverageTableColumns(daysBack);
  const { filterOptions } = useCoverageFilterOptions(daysBack);
  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    onCSVDownload,
    isExporting,
    totalItems,
    totalCritical,
    isFiltersFetching,
  } = useCoverageTableData({ search, daysBack });

  useViewLoadTracking({
    name: 'Coverage table data',
    loadDeps: [
      (data?.length && !(isFetching || isFiltersFetching) && filterOptions) ||
        null,
    ],
    contextId: search + daysBack + JSON.stringify(urlFilters),
  });

  const selectedIds = isSelectAll
    ? excludedItems
      ? filteredTableData
          .map((model) => model.id)
          .filter((id) => !excludedItems.includes(id))
      : 'all'
    : selectedItems;

  const selectedModels =
    selectedIds === 'all'
      ? filteredTableData
      : filteredTableData.filter((model) => selectedIds.includes(model.id));

  const chatContext = useMemo(() => {
    if (selectedModels.length === 0) return undefined;
    return {
      assetIds: selectedModels.map((m) => m.id),
      displayLabels: selectedModels.map((m) => m.name),
    };
  }, [selectedModels]);

  useChatContext(chatContext);

  const filterCounter = {
    [CatalogFilterOptions.All]: totalItems,
    [CatalogFilterOptions.CriticalAssets]: totalCritical,
  };

  const quickFilters: QuickFilter[] = [
    {
      name: `Critical assets (${Humanize.intComma(
        filterCounter[CatalogFilterOptions.CriticalAssets],
      )})`,
      loading: isFiltersFetching,
      icon: <CriticalAssetColorIcon width={20} />,
      filters: [
        {
          property: 'isCritical',
          operator: 'equals',
          value: ['true'],
        },
      ],
    },
  ];

  const clearSelections = () => {
    setSelectedItems([]);
    setExcludedItems([]);
    setIsSelectAll(false);
    setBulkEditWizardEditProperty(undefined);
  };

  useEffect(() => {
    if (selectedIds.length && selectedIds !== 'all') {
      const isSelectedIdsInTable = selectedIds.every((id) =>
        filteredTableData.find((tableItem) => tableItem.id === id),
      );

      if (!isSelectedIdsInTable) {
        clearSelections();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filteredTableData]);

  const additionalFilters: FilterOption[] = [
    {
      property: 'lineage',
      propertyLabel: 'Assets lineage',
      operators: ['upstreamFrom', 'downstreamFrom'],
      multiselect: true,
      options: filterOptions?.assetsLineage,
    },
    {
      property: 'isCritical',
      propertyLabel: 'Criticality',
      operators: ['equals'],
      multiselect: false,
      options: filterOptions?.criticality,
    },
  ];

  return (
    <TableWrapper>
      <TableHeader>
        <Flex gap="24px">
          <TableFilters
            data={data}
            columns={tableColumns}
            urlParamsPrefix={COVERAGE_TABLE_FILTERS}
            search={search}
            onSearch={setSearch}
            quickFilters={quickFilters}
            clearFiltersChipLabel={`All (${Humanize.intComma(
              filterCounter[CatalogFilterOptions.All],
            )})`}
            showDaysBackPicker
            daysBackRange={{ min: 1, max: 30 }}
            additionalFilters={additionalFilters}
            disabled={isLoading || isFiltersFetching}
          />
        </Flex>

        <Flex sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCSVDownload}
            startIcon={<DownloadIcon width={12} />}
            disabled={!data.length || isExporting}
          >
            {isExporting ? 'Exporting...' : 'CSV'}
          </Button>
          <ToggleColumnsMenu
            columns={tableColumns}
            localStorageKey={COVERAGE_TABLE_STORAGE_KEY}
          />
        </Flex>
      </TableHeader>

      <CoverageSummary sx={{ marginBottom: '16px' }} />

      <Table
        wrapperSx={{
          height: '100%',
          maxHeight: '100%',
        }}
        idKey="id"
        data={data}
        columns={tableColumns}
        onRowClick={({ id }) =>
          id === selectedId ? closeSidePanel() : openSidePanel('asset', id)
        }
        isLoading={isFetching}
        localStorageKey={COVERAGE_TABLE_STORAGE_KEY}
        urlParamsPrefix={COVERAGE_TABLE_FILTERS}
        search={search}
        selectedItems={selectedItems}
        isSelectAll={isSelectAll}
        excludedItems={excludedItems}
        onSelectItem={(items) => setSelectedItems(items)}
        onExcludeItem={(items) => setExcludedItems(items)}
        onSelectAllChange={(val) => setIsSelectAll(val)}
        emptyStateRenderer={TableEmptyState}
        onFilteredItemsChange={(items) => setFilteredTableData(items)}
        selectionColumn={canEdit}
        onScrollToBottom={() => fetchNextPage()}
        isControlled
        bottomScrollPadding={100}
      />

      <AssetsTableActionMenu
        selectedAssets={selectedModels.map((model) => model.id)}
        onOpenWizard={(value) => setBulkEditWizardEditProperty(value)}
        isEditable={selectedModels?.every((model) =>
          EDITABLE_ASSET_TYPES.includes(model.tableType.toLowerCase()),
        )}
        options={['test']}
        onClose={clearSelections}
      />

      <BulkEditWizard
        isOpen={!!bulkEditWizardEditProperty}
        editProperty={bulkEditWizardEditProperty}
        onClose={() => setBulkEditWizardEditProperty(undefined)}
        onSubmit={clearSelections}
        modelIds={selectedModels.map((m) => m.id)}
      />
    </TableWrapper>
  );
};

export default CoverageTable;
